import { movies, type Movie } from "@/mocks/movies";
import { categories, type MovieCategory } from "@/mocks/categories";

export type EraKey = "all" | "2020s" | "2010s" | "classic";
export type SortKey = "latest" | "popular" | "rating";

export const REGIONS = ["全部", "内地", "香港", "台湾"] as const;

export const ERA_OPTIONS: { key: EraKey; label: string }[] = [
  { key: "all", label: "全部年代" },
  { key: "2020s", label: "2020 年代" },
  { key: "2010s", label: "2010 年代" },
  { key: "classic", label: "经典回顾" },
];

export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新上映" },
  { key: "popular", label: "最多播放" },
  { key: "rating", label: "评分最高" },
];

export function getMovieById(id: string): Movie | undefined {
  return movies.find((movie) => movie.id === id);
}

export function getCategoryById(id: string): MovieCategory | undefined {
  return categories.find((category) => category.id === id);
}

export function getCategoryName(id: string): string {
  return getCategoryById(id)?.name ?? "未分类";
}

export function getMoviesByCategory(categoryId: string): Movie[] {
  return movies.filter((movie) => movie.categoryIds.includes(categoryId));
}

function matchesEra(year: number, era: EraKey): boolean {
  if (era === "all") return true;
  if (era === "2020s") return year >= 2020;
  if (era === "2010s") return year >= 2010 && year < 2020;
  return year < 2010;
}

export function filterMovies(
  source: Movie[],
  options: { region?: string; era?: EraKey; sort?: SortKey } = {}
): Movie[] {
  const { region = "全部", era = "all", sort = "latest" } = options;
  let result = source.filter((movie) => {
    const regionOk = region === "全部" || movie.region === region;
    const eraOk = matchesEra(movie.year, era);
    return regionOk && eraOk;
  });
  result = [...result].sort((a, b) => {
    if (sort === "popular") return b.views - a.views;
    if (sort === "rating") return b.rating - a.rating;
    return b.year - a.year;
  });
  return result;
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.synopsis,
      movie.director,
      movie.region,
      movie.tags.join(" "),
      getCategoryName(movie.categoryId),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getLatestMovies(limit = 20): Movie[] {
  return [...movies].sort((a, b) => b.year - a.year).slice(0, limit);
}

export function getPopularMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getFeaturedMovies(): Movie[] {
  return movies.filter((movie) => movie.featured);
}

export function getRelatedMovies(movie: Movie, limit = 6): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.categoryId === movie.categoryId) score += 3;
      score += item.categoryIds.filter((id) => movie.categoryIds.includes(id)).length * 2;
      score += item.tags.filter((tag) => movie.tags.includes(tag)).length * 2;
      if (item.region === movie.region) score += 1;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return `${views}`;
}